import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgButton from 'modules/Common/ui/CgButton';
import { useFreemium } from './FreemiumContext';
import http from 'helpers/requests';
import { cgToast, notifyError } from 'modules/Common/ui/toast';
import { useBranding } from 'modules/App/hooks/useBranding';

const CgArchivePopover = () => {
  const { t } = useTranslation('common');
  const { restrictions, triggerLimitExceeded } = useFreemium();
  const brand = useBranding();
  const [selectedInstallations, setSelectedInstallations] = useState([]);
  const [installations, setInstallations] = useState([]);
  const installationRestrictions = restrictions.filter(
    (r) => r && r.exceeded && r.resource === 'installations',
  );

  const limit = installationRestrictions.length > 0 ? installationRestrictions[0].limit : null;
  const count = installationRestrictions.length > 0 ? installationRestrictions[0].count : null;
  const exceededInstallations = count - limit > 0;
  // If the user has NOT exceeded their limit, do NOT show the modal

  const shouldShow = exceededInstallations && window.location.pathname !== '/upgrade';

  // Only fetch installations when we actually need to show the archive modal.
  // This avoids unnecessary calls to /installations/operator/search on routes
  // like /upgrade where backend trial checks are already enforced.
  useEffect(() => {
    if (!shouldShow) return;

    http.post('/installations/operator/search', { items: 100, archived: false }).then((res) => {
      setInstallations(res.data.installations);
    });
  }, [shouldShow]);

  if (brand.key !== 'carbonglance') return null;

  const handleToggleInstallation = (installationId) => {
    setSelectedInstallations((prevSelected) =>
      prevSelected.includes(installationId)
        ? prevSelected.filter((id) => id !== installationId)
        : [...prevSelected, installationId],
    );
  };

  const handleArchiveSelected = () => {
    if (selectedInstallations.length >= count - limit) {
      http
        .put('/installations/operator/archive', {
          installations: selectedInstallations,
        })
        .then((res) => {
          cgToast.success(t('common:toasts.success', 'Success'), {
            description: t(
              'common:archive.success',
              'Installations have been archived successfully',
            ),
          });
          setSelectedInstallations([]); // Clear selection
          triggerLimitExceeded(); // Refresh the restriction data
        })
        .catch(notifyError);
    }
  };

  return (
    <CgDialog
      open={shouldShow}
      // No dismiss handler existed for the react-bootstrap Modal either
      // (backdrop="static" + keyboard={false} blocked click/Esc, and no
      // onHide was wired) — an outside-interaction request here is a no-op,
      // same as before.
      onOpenChange={() => {}}
      closeOnOverlayClick={false}
      closeButton={false}
      size="lg"
      title={t('common:archive.title', 'Archive Extra Installations')}
      footer={
        <div className="flex items-center justify-end gap-2">
          <CgButton
            variant="danger"
            onClick={handleArchiveSelected}
            disabled={selectedInstallations.length < count - limit}
            label={t('common:archive.archiveSelected', 'Archive Selected')}
          />
          <CgButton
            variant="premium"
            onClick={() => (window.location.href = '/upgrade')}
            label={t('common:archive.upgradeNow', 'Upgrade Now')}
          />
        </div>
      }
    >
      <p className="text-secondaryColor font-semibold">
        {t(
          'common:archive.limitExceededBody',
          'You have exceeded your installation limit. You probably downgraded your plan or part of your plan expired. Please archive extra installations or upgrade your plan.',
        )}
      </p>
      <br />
      <p className="text-secondaryColor font-semibold">
        {t('common:archive.selectedCount', {
          selected: selectedInstallations.length,
          available: count - limit,
          defaultValue: 'You have selected {{selected}}/{{available}} installations',
        })}
      </p>
      <hr />
      <br />
      {installations.length === 0 ? (
        <p>{t('common:archive.noneAvailable', 'No installations available for archiving.')}</p>
      ) : (
        <ul className="rounded-md border border-black/[.125] divide-y divide-black/[.125]">
          {installations.map((installation) => (
            <li
              key={installation._id}
              className={`flex justify-between items-center cursor-pointer px-4 py-2 ${
                selectedInstallations.includes(installation._id)
                  ? 'bg-primaryColor text-white'
                  : 'bg-white hover:bg-gray-50'
              }`}
              onClick={() => handleToggleInstallation(installation._id)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <span>
                {installation.name ||
                  t('common:archive.installationIdFallback', {
                    id: installation._id,
                    defaultValue: 'Installation ID: {{id}}',
                  })}
              </span>
              <input
                type="checkbox"
                checked={selectedInstallations.includes(installation._id)}
                readOnly
              />
            </li>
          ))}
        </ul>
      )}
    </CgDialog>
  );
};

export default CgArchivePopover;
